<template>
	<div id="app">
		<Notification />
		<router-view />
		<SupportModal ref="supportModal" />
		<ShareModal ref="shareModal" />
		<StepModal ref="stepModal" />
		<AuthenticationNavigationController />
		<PrivateChefModal ref="privateChefModal" />
		<MultiDeliverySlotEventModal ref="multiDeliverySlotEventModal" />
		<CouponModal ref="couponModal" />
	</div>
</template>

<script>
import { eventBus, EventBusEvents } from "@/eventBus"
import { ExperimentType } from "@/enums/ExperimentType"
import api from "@/api"
import SupportModal from "@/components/modals/SupportModal.vue"
import Notification from "@/components/Notification.vue"
import StepModal from "@/components/modals/experiments/StepModal.vue"
import ShareModal from "@/components/modals/ShareModal.vue"
import AuthenticationNavigationController from "@/components/AuthenticationNavigationController.vue"
import PrivateChefModal from "@/components/modals/experiments/PrivateChefModal.vue"
import MultiDeliverySlotEventModal from "@/components/modals/experiments/MultiDeliverySlotEventModal.vue"
import CouponModal from "@/components/modals/CouponModal.vue"

export default {
	name: "App",
	components: {
		SupportModal,
		Notification,
		StepModal,
		ShareModal,
		AuthenticationNavigationController,
		PrivateChefModal,
		MultiDeliverySlotEventModal,
		CouponModal
	},
	data: function () {
		return {
			trackedBusiness: false,
			mounted: false
		}
	},
	computed: {
		business() {
			return this.$store.state.business
		},
		exptType() {
			return this.mounted && this.$route.query.exptType
				? this.$route.query.exptType
				: null
		}
	},
	beforeCreate: function () {
		if (!console.defaultError) {
			console.defaultError = console.error.bind(console)
			console.error = function () {
				if (
					arguments &&
					arguments[0] &&
					arguments[0].includes &&
					arguments[0].includes("Mixpanel error:") &&
					window["bugsnagClient"] &&
					window["bugsnagClient"].notify
				) {
					window["bugsnagClient"].notify(arguments[0], {
						metaData: {
							reason: arguments
						}
					})
				}
				console.defaultError.apply(console, arguments)
			}
		}
		if (this.$store.state.user && this.$store.state.user.id != 0) {
			const business = this.$store.state.business
			const user = this.$store.state.user
			mixpanel.people.set({
				$name: user.username,
				$email: user.email,
				$phone: user.phone,
				business_id: user.businessId,
				sms_opt_in: user.smsOptIn,
				is_verified: user.isVerified
			})
			if (business && business.id == user.businessId) {
				mixpanel.people.set_once({
					business_type: business.type,
					business_slug: business.slug
				})
			}
			mixpanel.identify(user.id)
		}
	},
	created: function () {
		this.configureModals()
		this.listenToState()
	},
	mounted: function () {
		if (this.$route.params.slug) {
			this.$store.dispatch("initHome", this.$route.params.slug)
			this.bugsnag.setUser(
				this.$store.state.user ? this.$store.state.user.id : 0,
				undefined,
				this.$route.params.slug
			)
		}
		this.mounted = true
	},
	methods: {
		configureModals() {
			eventBus.on(EventBusEvents.OPEN_SUPPORT_MODAL, () => {
				this.$refs.supportModal.open()
			})
			eventBus.on(EventBusEvents.OPEN_SHARE_MODAL, (target) => {
				this.$refs.shareModal.open({
					target: target
				})
			})
			eventBus.on(EventBusEvents.OPEN_COUPON_MODAL, () => {
				this.$refs.couponModal.open()
			})
		},
		listenToState() {
			//Watch for price changes and recalculate coupon
			this.$store.watch(
				(state, getters) => getters.price,
				() => {
					if (
						this.$store.state.coupon &&
						this.$store.state.coupon.id != 0 &&
						this.$store.state.coupon.code &&
						this.$store.state.coupon.code.length > 0
					) {
						this.$store.dispatch(
							"calcDiscount",
							this.$store.state.coupon.code
						).catch(() => {})
					}
				}
			)
		},
		trackAppEntry() {
			if (this.trackedBusiness) {
				return
			}
			this.trackedBusiness = true
			if (this.$route.meta.startsSession) {
				this.$store.dispatch("sendMixpanelEvent", {
					msg: "Session Start",
					data: {
						slug: this.$route.params.slug
					}
				})
			} else if (this.$route.name == "embed") {
				this.$store.dispatch("sendMixpanelEvent", {
					msg: "Embed Page View",
					data: {
						slug: this.$route.params.slug
					}
				})
			}
		}
	},
	watch: {
		business: {
			immediate: true,
			handler(newVal) {
				if (newVal.slug == this.$route.params.slug) {
					this.trackAppEntry()
				}
			}
		},
		exptType: {
			immediate: true,
			handler(type) {
				switch (type) {
					case ExperimentType.PREORDER:
					case ExperimentType.HAPPY_HOUR: {
						this.$refs.stepModal.open({ type: type })
						break
					}
					case ExperimentType.ON_PREM: {
						if (
							this.$route.query.eventId &&
							this.$route.params.slug
						) {
							if (
								!this.$store.state.business ||
								!this.$store.state.business.menus
							) {
								api.getBusiness(this.$route.params.slug).then(
									(response) => {
										if (
											response.status &&
											response.data.menus
										) {
											const eventMenu =
												response.data.menus.find(
													(menu) =>
														menu.event_id ==
														this.$route.query
															.eventId
												)
											if (eventMenu) {
												this.$refs.stepModal.open({
													type: type
												})
											}
										}
									}
								)
							} else {
								const eventMenu =
									this.$store.state.business.menus.find(
										(menu) =>
											menu.event_id ==
											this.$route.query.eventId
									)
								if (eventMenu) {
									this.$refs.stepModal.open({ type: type })
								}
							}
						}
						break
					}
					case ExperimentType.REFERRAL_SMS: {
						if (this.$store.state.user.isVerified) {
							this.$store
								.dispatch("getUserCoupons")
								.then(() => {
									this.$refs.stepModal.open({ type: type })
								})
								.catch((e) => {
									this.bugsnag.notify(
										"Failed to show referral experiment",
										(event) => {
											event.addMetadata("reason", {
												error: e,
												type: type
											})
										}
									)
								})
						}
						break
					}
					case ExperimentType.PRIVATE_CHEF: {
						this.$refs.privateChefModal.open()
						break
					}
					case ExperimentType.MULTIPLE_EVENT_TIMES: {
						this.$refs.multiDeliverySlotEventModal.open()
						break
					}
				}
			}
		}
	}
}
</script>


